import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DxSelectBoxModule, DxDateRangeBoxModule, DxLoadPanelModule } from 'devextreme-angular';
import { EffectivenessService } from './effectiveness.service';
import { BreakdownContext, MonthlySummaryResponse, ProductBreakdownResponse } from './effectiveness.models';
import { MonthlySummaryComponent } from './monthly-summary/monthly-summary.component';
import { ProductBreakdownComponent } from './product-breakdown/product-breakdown.component';
import { PageHeaderComponent } from 'src/app/shared/page-header/page-header.component';

@Component({
  selector: 'app-effectiveness',
  templateUrl: './effectiveness.component.html',
  styleUrls: ['./effectiveness.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    DxSelectBoxModule,
    DxDateRangeBoxModule,
    DxLoadPanelModule,
    MonthlySummaryComponent,
    ProductBreakdownComponent,
    PageHeaderComponent
  ]
})
export class EffectivenessComponent implements OnInit {

  summaryData: MonthlySummaryResponse | null = null;
  isLoading = false;

  showBreakdown = false;
  breakdownData: ProductBreakdownResponse | null = null;
  breakdownLoadFailed = false;
  breakdownContext: BreakdownContext | null = null;
  private breakdownRequestId = 0;

  buyingGroups: any[] = [];
  promotionTypes: any[] = [];

  startDate: Date;
  endDate: Date;
  selectedBuyingGroup: number | null = null;
  selectedPromotionType: string | null = null;
  selectedBuyingGroupName: string | null = null;

  constructor(private effectivenessService: EffectivenessService) {
    // Default reporting period: the current calendar month
    const now = new Date();
    this.startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    this.endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  }

  async ngOnInit(): Promise<void> {
    await this.loadFilterData();
    this.loadData();
  }

  async loadFilterData(): Promise<void> {
    const [buyingGroups, promotionTypes] = await Promise.all([
      this.effectivenessService.loadBuyingGroups(),
      this.effectivenessService.loadPromotionTypes()
    ]);
    this.buyingGroups = buyingGroups;
    this.promotionTypes = promotionTypes;
  }

  async loadData(): Promise<void> {
    this.isLoading = true;
    this.summaryData = await this.effectivenessService.loadMonthlySummary(
      this.formatDate(this.startDate),
      this.formatDate(this.endDate),
      this.selectedBuyingGroup,
      this.selectedPromotionType
    );
    this.isLoading = false;
  }

  onRangeChanged(e: any): void {
    const [start, end] = e.value ?? [null, null];
    // Range box fires while only one end is picked — wait for a complete range
    if (start && end) {
      this.resetBreakdown();
      this.startDate = start;
      this.endDate = end;
      this.loadData();
    }
  }

  onBuyingGroupChanged(e: any): void {
    this.resetBreakdown();
    this.selectedBuyingGroup = e.value;
    this.selectedBuyingGroupName = this.getSelectedBuyingGroupName();
    this.loadData();
  }

  onPromotionTypeChanged(e: any): void {
    this.resetBreakdown();
    this.selectedPromotionType = e.value;
    this.loadData();
  }

  async onBreakdownRequested(context: BreakdownContext): Promise<void> {
    const requestId = ++this.breakdownRequestId;
    this.breakdownContext = context;
    this.breakdownData = null;
    this.breakdownLoadFailed = false;
    this.showBreakdown = true;
    this.isLoading = true;
    const data = await this.effectivenessService.loadPromotionBreakdown(context.promotionId);
    if (requestId !== this.breakdownRequestId) return;
    this.breakdownData = data;
    if (this.breakdownData === null) {
      this.breakdownLoadFailed = true;
    }
    this.isLoading = false;
  }

  onBreakdownBack(): void {
    this.resetBreakdown();
  }

  private resetBreakdown(): void {
    this.breakdownRequestId++;
    this.showBreakdown = false;
    this.breakdownData = null;
    this.breakdownLoadFailed = false;
    this.breakdownContext = null;
  }

  formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  getSelectedBuyingGroupName(): string | null {
    if (this.selectedBuyingGroup === null) return null;
    const group = this.buyingGroups.find((bg: any) => bg.id === this.selectedBuyingGroup);
    return group ? group.buying_group_name : null;
  }
}
